import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreDirectivesModule } from '@core/directives/directives';
import { TranslocoService } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { SelectFieldComponent } from 'app/main/pages/contract-control/editable-fields/select-field/select-field.component';
import { TextFieldComponent } from 'app/main/pages/contract-control/editable-fields/text-field/text-field.component';
import { IField, IGroup } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { DynamicCategorySelectComponent } from 'app/main/shared/shared-elements/dynamic-category-select/dynamic-category-select.component';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IDocumentDetailRootDTO } from '../../../dto/document-details.dto';
import { DynamicFieldComponent } from '../dynamic-field/dynamic-field.component';
import { EditorEmitterService } from 'app/main/pages/events/editor-emitter.service';
import { Subject, takeUntil } from 'rxjs';
import { DocumentTypeFieldNameEnum } from 'app/main/pages/settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';
import { GlobalService } from 'app/global/global.service';
import { ConfirmationTypeEnum } from 'app/main/pages/general-editor-tabs/recipients-tab/recipient-tab.dto';

export interface SignatureSectionUpdateEvent {
  action: 'signingDeadlineChanged';
  data: string;
}

export interface DocumentKeyValidationState {
  dkType: string;
  documentKey: number;
  dkSendViaSms: number;
}

interface DropdownDTO { id: number, name: string | string[], value: string }

@Component({
  selector: 'app-signature-request-section',
  standalone: true,
  imports: [
    CommonModule,
    CoreDirectivesModule,
    TranslocoRootModule,
    NgSelectModule,
    DynamicCategorySelectComponent,
    TextFieldComponent,
    SelectFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    DynamicFieldComponent
  ],
  templateUrl: './signature-request-section.component.html',
  styleUrl: './signature-request-section.component.scss',
})
export class SignatureRequestSectionComponent implements OnInit, OnChanges, OnDestroy {

  @Output() updateDocument = new EventEmitter<void | SignatureSectionUpdateEvent>();
  @Output() addToContractCardEnabled = new EventEmitter<void>();
  @Output() documentKeyValidationChanged = new EventEmitter<DocumentKeyValidationState>();

  @Input({ required: true }) group: IGroup;
  @Input() sectionName: string = 'send-document';
  @Input({ required: true }) documentData: IDocumentDetailRootDTO;
  @Input() formGroup: import('@angular/forms').FormGroup;
  @Input() hasFullAccessToDocument: boolean = false;
  @Input() canEditDocument: boolean = false;

  get isSigned(): boolean {
    return this.documentData?.template?.status === 'signed';
  }

  get canEdit(): boolean {
    return this.canEditDocument && !this.isSigned && this.documentData?.template?.can_edit === true;
  }

  public documentKeyType: DropdownDTO[] = [
    { id: 1, name: [this._translocoService.translate("Password - label (general) - Password")], value: 'password' },
    { id: 2, name: [this._translocoService.translate("PIN Via SMS option (Template/Document - section) - PIN Via SMS")], value: 'pin_code' },
    { id: 3, name: 'bankID', value: 'bank_id' }
  ];
  public languages: DropdownDTO[] = [
    { id: 1, name: 'English', value: 'english' },
    { id: 2, name: 'Swedish', value: 'swedish' },
    { id: 3, name: 'German', value: 'german' },
    { id: 4, name: 'Finnish', value: 'finnish' },
    { id: 5, name: 'French', value: 'french' },
    { id: 7, name: 'Norwegian', value: 'norwegian' },
    { id: 8, name: 'Danish', value: 'danish' },
    { id: 9, name: 'Spanish', value: 'spanish' }
  ];;

  private destroyed = new Subject<void>();
  private controlSubscriptionsInitialized = false;

  constructor(
    private _translocoService: TranslocoService, 
    private editorEmitterService: EditorEmitterService,
    private _globalService: GlobalService
  ) { }



  ngOnInit(): void {
    this.initializeFormControls();
    this.initializeControlValueChangeSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && !changes['group'].firstChange) {
      this.initializeFormControls();
      this.initializeControlValueChangeSubscriptions();
    }
  }



  /**
   * Initialize form controls based on group fields
   */
  private initializeFormControls(): void {
    this.group.fields.forEach(field => {
      let value = field.value;

      if (field.field_name === DocumentTypeFieldNameEnum.Language) {
        value = value ?? 'english';
      }
      if (field.field_name === DocumentTypeFieldNameEnum.DocumentKey) {
        value = value ?? 0;
      }

      if (field.field_name === DocumentTypeFieldNameEnum.ExternalChat) {
        value = value ?? 0;
      }
      if (field.field_name === DocumentTypeFieldNameEnum.ActiveBcc) {
        value = value ?? 0;
      }
      if (field.field_name === DocumentTypeFieldNameEnum.Currency) {
        value = value ?? this._globalService.getSystemCurrency();
      }

      this.addFieldControl(field.field_name, value);
      this.handleSpecialFieldControls(field);
    });

    this.setTheValidationForDocumentKey(this.formGroup.get('dk_type')?.value ?? 'off');
    this.formGroup.updateValueAndValidity();
  }


  /**
   * Add a single form control
   */
  private addFieldControl(fieldName: string, value: any, defaultValue: any = null): void {
    if (this.formGroup.contains(fieldName)) {
      this.formGroup.get(fieldName).setValue(value ?? defaultValue);
    } else {
      this.formGroup.addControl(fieldName, new FormControl(value ?? defaultValue));
    }
  }




  /**
   * Handle special field controls based on field type
   */
  private handleSpecialFieldControls(field: IField): void {
    const fieldHandlers: { [key: string]: () => void } = {
      'document_key': () => this.addDocumentKeyControls(field),
      'signing_reminder': () => this.addSigningReminderControls(field),
      'active_bcc': () => this.addBccControls(field),
      'sender_confirmation_type': () => this.addSenderConfirmationControls(field),
      'currency': () => this.addCurrencyControls(field)
    };
    const handler = fieldHandlers[field.field_name];
    if (handler) {
      handler();
    }
  }


  /**
   * Add document key related controls
   */
  private addDocumentKeyControls(field: IField): void {
    this.addFieldControl('dk_type', field?.dk_type ?? "password");
    this.addFieldControl('dk_password', field?.dk_password ?? "");
    this.addFieldControl('dk_password_hint', field?.dk_password_hint ?? "");
    this.addFieldControl('dk_send_via_sms', field?.dk_send_via_sms ?? 0);
  }



  /**
   * Add signing reminder related controls
   */
  private addSigningReminderControls(field: IField): void {
    this.addFieldControl('recurring', field?.recurring ?? 0);
  }



  /**
   * Add currency related controls
   */
  private addCurrencyControls(field: IField): void {
    this.addFieldControl('currency', field.value);
  }

  /**
   * Add BCC settings controls
   */
  private addBccControls(field: IField): void {
    this.formGroup.addControl('bcc_settings', new FormGroup({
      subject: new FormControl(field?.bcc_settings?.subject ?? null),
      email: new FormControl(field?.bcc_settings?.email ?? null, this._globalService.commaSeparatedEmailsValidator()),
    }));
  }


  /**
   * Add sender confirmation type control
   */
  private addSenderConfirmationControls(field: IField): void {
    if (this.formGroup.get(DocumentTypeFieldNameEnum.SenderConfirmationType)) {
      this.formGroup.removeControl(DocumentTypeFieldNameEnum.SenderConfirmationType);
    }
    this.addFieldControl(DocumentTypeFieldNameEnum.SenderConfirmationType, field.value, ConfirmationTypeEnum.EMAIL);
  }

  private initializeControlValueChangeSubscriptions(): void {
    if (this.controlSubscriptionsInitialized || !this.formGroup) {
      return;
    }

    this.controlSubscriptionsInitialized = true;

    this.formGroup.get('dk_type')?.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((value: string) => {
        this.formGroup.get('dk_send_via_sms')?.setValue(0, { emitEvent: false });
        this.setTheValidationForDocumentKey(value);
      });

    this.formGroup.get('currency')?.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((currency) => {
        this.editorEmitterService.setDocumentCurrency(currency);
      });

    this.formGroup.get('add_to_contract_card')?.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((value: boolean) => {
        if (value) {
          this.addToContractCardEnabled.emit();
        }
      });

    this.formGroup.get('dk_send_via_sms')?.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.setTheValidationForDocumentKey('password');
      });

    this.formGroup.get('document_key')?.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => {
        this.setTheValidationForDocumentKey('off');
      });
  }

  private setTheValidationForDocumentKey(_value: string): void {
    this.formGroup.get('dk_password')?.updateValueAndValidity({ emitEvent: false });
    this.formGroup.get('dk_password_hint')?.updateValueAndValidity({ emitEvent: false });
    this.emitDocumentKeyValidationState(_value);
  }

  private emitDocumentKeyValidationState(dkType?: string): void {
    this.documentKeyValidationChanged.emit({
      dkType: dkType ?? this.formGroup.get('dk_type')?.value ?? 'off',
      documentKey: Number(this.formGroup.get('document_key')?.value ?? 0),
      dkSendViaSms: Number(this.formGroup.get('dk_send_via_sms')?.value ?? 0),
    });
  }


  handleFieldAction(event: { action: string; data: any }): void {
    switch (event.action) {
      case 'onSigningDeadlineChange':
        this.onSigningDeadlineChange(event.data);
        break;
      case 'onSigningReminderChange':
        this.onSigningReminderChange(event.data);
        break;
      case 'onRecurringChange':
        this.onRecurringChange(event.data);
        break;
      case 'onCurrencyChange':
        this.onCurrencyChange(event.data);
        break;
      case 'onLanguageChange':
        this.onLanguageChange(event.data);
        break;
      case 'onDocumentKeyChange':
        this.onDocumentKeyChange(event.data);
        break;
      case 'onDocumentKeyTypeChange':
        this.onDocumentKeyTypeChange(event.data);
        break;
      case 'onDKPasswordChange':
        this.onDKPasswordChange(event.data);
        break;
      case 'onDKPasswordHintChange':
        this.onDKPasswordHintChange(event.data);
        break;
      case 'onDKSendViaSMSChange':
        this.onDKSendViaSMSChange(event.data);
        break;
      case 'onExternalChatChange':
        this.onExternalChatChange(event.data);
        break;
      case 'onInvitationMessageChange':
        this.onInvitationMessageChange(event.data);
        break;
      case 'onConfirmationMessageChange':
        this.onConfirmationMessageChange(event.data);
        break;
      case 'onChangeActiveBcc':
        this.onChangeActiveBcc(event.data);
        break;
      case 'onChangeAllowUploadFile':
        this.onChangeAllowUploadFile(event.data);
        break;
      case 'onChangeConfirmationType':
        this.onChangeConfirmationType(event.data);
        break;
    }
  }


  onSigningDeadlineChange(value: string): void {
    this.formGroup.get('signing_deadline')?.setValue(value);
    this.updateDocument.emit({ action: 'signingDeadlineChanged', data: value });
  }

  onSigningReminderChange(value: string): void {
    this.formGroup.get('signing_reminder')?.setValue(value);
    this.updateDocument.emit();
  }

  onRecurringChange(value: boolean): void {
    this.formGroup.get('recurring')?.setValue(value);
    this.updateDocument.emit();
  }

  onCurrencyChange(value: string): void {
    this.formGroup.get('currency')?.setValue(value);
    this.updateDocument.emit();
  }

  onLanguageChange($event: { id: number, name: string, value: string }): void {
    this.formGroup.get('language')?.setValue($event.value);
    this.updateDocument.emit();
  }

  onDocumentKeyChange(value: boolean): void {
    this.formGroup.get('document_key')?.setValue(value);
    this.updateDocument.emit();
  }

  onDocumentKeyTypeChange($event: { id: number, name: string | string[], value: string }): void {
    this.formGroup.get('dk_type')?.setValue($event.value);
    this.updateDocument.emit();
  }

  onDKPasswordChange(value: string): void {
    this.formGroup.get('dk_password')?.setValue(value);
    this.updateDocument.emit();
  }

  onDKPasswordHintChange(value: string): void {
    this.formGroup.get('dk_password_hint')?.setValue(value);
    this.updateDocument.emit();
  }

  onDKSendViaSMSChange(value: boolean): void {
    this.formGroup.get('dk_send_via_sms')?.setValue(value);
    this.updateDocument.emit();

  }

  onExternalChatChange(value: boolean): void {
    this.formGroup.get('external_chat')?.setValue(value);
    this.updateDocument.emit();
  }

  onInvitationMessageChange(value: string): void {
    this.formGroup.get('invitation_message')?.setValue(value);
    this.updateDocument.emit();
  }

  onConfirmationMessageChange(value: string): void {
    this.formGroup.get('confirmation_message')?.setValue(value);
    this.updateDocument.emit();
  }

  onChangeActiveBcc(value: boolean): void {
    this.formGroup.get('active_bcc')?.setValue(value);
    this.updateDocument.emit();
  }

  onChangeAllowUploadFile(value: boolean): void {
    this.formGroup.get('allow_upload_file')?.setValue(value);
    this.updateDocument.emit();
  }

  onChangeConfirmationType(value: string): void {
    this.formGroup.get('sender_confirmation_type')?.setValue(value);
    this.updateDocument.emit();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
